import { createBrowserRouter, createRoutesFromElements, RouterProvider, Route, Navigate } from "react-router-dom";
import "@/App.css";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";
import FileSizeLimitModal from "@/components/FileSizeLimitModal";
import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";
import Layout from "@/components/Layout";
import SuperAdminLayout from "@/components/SuperAdminLayout";
import Dashboard from "@/pages/Dashboard";
import ProductEdit from "@/pages/ProductEdit";
import ProductImport from "@/pages/ProductImport";
import Archives from "@/pages/Archives";
import Clients from "@/pages/Clients";
import Suppliers from "@/pages/Suppliers";
import SupplierDetail from "@/pages/SupplierDetail";
import ClientEdit from "@/pages/ClientEdit";
import Downloads from "@/pages/Downloads";
import QRLayoutPage from "@/pages/QRLayoutPage";
import VetrinaLayoutPage from "@/pages/VetrinaLayoutPage";
import BrandCatalog from "@/pages/BrandCatalog";
import BrandProductDetail from "@/pages/BrandProductDetail";
import PublicProduct from "@/pages/PublicProduct";
import PublicSafetySheet from "@/pages/PublicSafetySheet";
import Disabled from "@/pages/Disabled";
import Landing from "@/pages/Landing";
import SuperAdmin from "@/pages/SuperAdmin";
import SuperAdminTenant from "@/pages/SuperAdminTenant";
import SuperAdminPictograms from "@/pages/SuperAdminPictograms";
import Profile from "@/pages/Profile";
import PolicyPage from "@/pages/PolicyPage";
import PrivacyPolicyAdmin from "@/pages/PrivacyPolicyAdmin";
import SupportWorkspace from "@/pages/SupportWorkspace";
import SupportAttachmentPage from "@/pages/SupportAttachmentPage";
import { ContractGate } from "@/components/ContractGate";

function Protected({ children }) {
  const { user, loading, isSuperadmin } = useAuth();
  if (loading) return <div className="p-10 text-sm text-gray-500">Caricamento...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (isSuperadmin) return <Navigate to="/admin" replace />;
  return <ContractGate>{children}</ContractGate>;
}

function DownloadsOnlyWhenActive() {
  const { subscriptionDisabled, user } = useAuth();
  return subscriptionDisabled || user?.impersonation ? <Navigate to="/products" replace /> : <Downloads />;
}

function SuperadminOnly({ children }) {
  const { user, loading, isSuperadmin } = useAuth();
  if (loading) return <div className="p-10 text-sm text-gray-500">Caricamento...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!isSuperadmin) return <Navigate to="/products" replace />;
  return children;
}

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/public/:slug" element={<PublicProduct />} />
      <Route path="/public/:slug/safety" element={<PublicSafetySheet />} />
      <Route path="/p/:slug" element={<PublicProduct />} />
      <Route path="/p/:slug/safety" element={<PublicSafetySheet />} />
      <Route path="/p/:slug/packaging/:documentId" element={<PublicSafetySheet variant="packaging" />} />
      <Route path="/b/:slug" element={<BrandCatalog />} />
      <Route path="/b/:slug/p/:productSlug" element={<BrandProductDetail />} />
      <Route path="/disabled" element={<Disabled />} />
      <Route path="/privacy-policy" element={<PolicyPage kind="privacy" />} />
      <Route path="/cookie-policy" element={<PolicyPage kind="cookie" />} />
      <Route path="/termini-di-servizio" element={<PolicyPage kind="terms" />} />
      <Route path="/admin" element={<SuperadminOnly><SuperAdminLayout /></SuperadminOnly>}>
        <Route index element={<SuperAdmin />} />
        <Route path="policies" element={<Navigate to="/admin/contracts" replace />} />
        <Route path="contracts" element={<PrivacyPolicyAdmin />} />
        <Route path="pictograms" element={<SuperAdminPictograms />} />
        <Route path="assistenza" element={<SupportWorkspace />} />
        <Route path="assistenza/:ticketId" element={<SupportWorkspace />} />
        <Route path="assistenza/:ticketId/allegato/:fileId" element={<SupportAttachmentPage />} />
        <Route path="tenants/:id" element={<SuperAdminTenant />} />
      </Route>
      <Route path="/app" element={<Protected><Layout /></Protected>}>
        <Route index element={<Navigate to="/products" replace />} />
      </Route>
      <Route path="/products" element={<Protected><Layout /></Protected>}>
        <Route index element={<Dashboard />} />
        <Route path="import" element={<ProductImport />} />
        <Route path="new" element={<ProductEdit />} />
        <Route path=":id" element={<ProductEdit />} />
      </Route>
      <Route element={<Protected><Layout /></Protected>}>
        <Route path="archives" element={<Archives />} />
        <Route path="clients" element={<Clients />} />
        <Route path="suppliers" element={<Suppliers />} />
        <Route path="suppliers/:id" element={<SupplierDetail />} />
        <Route path="clients/:id" element={<ClientEdit />} />
        <Route path="downloads" element={<DownloadsOnlyWhenActive />} />
        <Route path="qr-layout" element={<QRLayoutPage />} />
        <Route path="vetrina-layout" element={<VetrinaLayoutPage />} />
        <Route path="profile" element={<Profile />} />
        <Route path="assistenza" element={<SupportWorkspace />} />
        <Route path="assistenza/nuovo" element={<SupportWorkspace />} />
        <Route path="assistenza/:ticketId" element={<SupportWorkspace />} />
        <Route path="assistenza/:ticketId/allegato/:fileId" element={<SupportAttachmentPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Route>
  )
);

function App() {
  return (
    <AuthProvider>
      <Toaster position="top-right" />
      <FileSizeLimitModal />
      <RouterProvider router={router} />
    </AuthProvider>
  );
}

export default App;
